import type { Block, EntityRef, EntryKind, EntryLink, EntrySection, EntryStatus, GuideEntry, RewardGroup, RewardLine } from '../types/guide'

/*
 * Plantillas de fichas. Cada campo es opcional: si no hay dato confirmado,
 * el apartado no se crea. Nunca se rellenan huecos con suposiciones.
 */

type BaseInput = {
  id: string
  categoryId: string
  title: string
  summary: string
  kindLabel?: string
  aliases?: string[]
  tags?: string[]
  image?: string
  entity?: EntityRef
  status?: EntryStatus
  updatedAt?: string
  related?: string[]
}

const has = (a?: unknown[]) => !!a && a.length > 0

function compact<T>(items: (T | false | null | undefined)[]): T[] {
  return items.filter(Boolean) as T[]
}

function section(id: string, title: string, blocks: (Block | false | null | undefined)[]): EntrySection | null {
  const b = compact(blocks)
  return b.length ? { id, title, blocks: b } : null
}

function base(i: BaseInput, kind: EntryKind, sections: (EntrySection | null)[]): GuideEntry {
  return {
    id: i.id,
    categoryId: i.categoryId,
    kind,
    kindLabel: i.kindLabel,
    title: i.title,
    summary: i.summary,
    aliases: i.aliases,
    tags: i.tags,
    image: i.image,
    entity: i.entity,
    status: i.status ?? 'available',
    updatedAt: i.updatedAt,
    related: i.related,
    sections: compact(sections),
  }
}

export type ItemInput = BaseInput & {
  description?: string[]
  classes?: string[]
  requirements?: string[]
  sources?: EntryLink[]
  uses?: string[]
}

export function buildItemEntry(i: ItemInput): GuideEntry {
  return base(i, 'item', [
    section('que-es', 'Qué es y para qué sirve', [has(i.description) && { type: 'text', paragraphs: i.description! }]),
    section('clases', 'Clases compatibles', [has(i.classes) && { type: 'list', items: i.classes! }]),
    section('requisitos', 'Requisitos', [has(i.requirements) && { type: 'list', items: i.requirements! }]),
    section('donde-conseguir', 'Dónde se consigue', [has(i.sources) && { type: 'links', items: i.sources! }]),
    section('usos', 'Usos en crafteos y mejoras', [has(i.uses) && { type: 'list', items: i.uses! }]),
  ])
}

export type EventInput = BaseInput & {
  description?: string[]
  requirements?: string[]
  location?: string
  mapNames?: string[]
  howTo?: string[]
  mechanics?: string[]
  monsters?: string
  frequency?: string
  rewards?: RewardGroup[]
  rules?: string[]
  scheduleEntryId?: string
  scheduleText?: string
}

export function buildEventEntry(i: EventInput): GuideEntry {
  const facts = compact([
    i.monsters && { label: 'Por aparición', value: i.monsters },
    i.frequency && { label: 'Frecuencia', value: i.frequency },
  ])
  return base(i, 'event', [
    section('descripcion', 'Descripción', [has(i.description) && { type: 'text', paragraphs: i.description! }]),
    section('requisitos', 'Requisitos de participación', [has(i.requirements) && { type: 'list', items: i.requirements! }]),
    section('ubicacion', 'Mapas y ubicación', [
      !!i.location && { type: 'facts', items: [{ label: 'Ubicación', value: i.location }] },
      has(i.mapNames) && { type: 'worldMaps', names: i.mapNames! },
    ]),
    section('como-participar', 'Cómo participar', [has(i.howTo) && { type: 'steps', items: i.howTo! }]),
    section('mecanica', 'Objetivo y mecánica', [has(i.mechanics) && { type: 'text', paragraphs: i.mechanics! }]),
    section('monstruos', 'Monstruos involucrados', [facts.length > 0 && { type: 'facts', items: facts }]),
    section('recompensas', 'Recompensas y drops', [has(i.rewards) && { type: 'rewards', groups: i.rewards! }]),
    section('reglas', 'Reglas', [has(i.rules) && { type: 'list', items: i.rules! }]),
    section('horarios', 'Cómo consultar sus horarios', [
      !!i.scheduleText && { type: 'callout', text: i.scheduleText },
      !!i.scheduleEntryId && { type: 'links', items: [{ entryId: i.scheduleEntryId }] },
    ]),
  ])
}

export type MechanicInput = BaseInput & {
  explanation?: string[]
  steps?: string[]
  costs?: string[]
  examples?: string[]
}

export function buildMechanicEntry(i: MechanicInput): GuideEntry {
  return base(i, 'mechanic', [
    section('explicacion', 'Explicación', [has(i.explanation) && { type: 'text', paragraphs: i.explanation! }]),
    section('pasos', 'Pasos', [has(i.steps) && { type: 'steps', items: i.steps! }]),
    section('costos', 'Costos y requisitos', [has(i.costs) && { type: 'list', items: i.costs! }]),
    section('ejemplos', 'Ejemplos', [has(i.examples) && { type: 'text', paragraphs: i.examples! }]),
  ])
}

export type MapInput = BaseInput & {
  description?: string[]
  invasions?: EntryLink[]
}

export function buildMapEntry(i: MapInput): GuideEntry {
  return base(i, 'map', [
    section('descripcion', 'Descripción', [has(i.description) && { type: 'text', paragraphs: i.description! }]),
    section('invasiones', 'Invasiones en este mapa', [has(i.invasions) && { type: 'links', items: i.invasions! }]),
  ])
}

/**
 * Convierte un texto de recompensa en una línea estructurada:
 * "20%: Wizard’s Ring." → probabilidad 20 · "2 joyas garantizadas…" → garantizada.
 */
export function parseRewardLine(raw: string, resolve: (label: string) => string | undefined): RewardLine {
  const text = raw.trim().replace(/\.$/, '')
  const prefixed = text.match(/^(\d+)%:\s*(.+)$/)
  if (prefixed) return { label: prefixed[2], chance: Number(prefixed[1]), entryId: resolve(prefixed[2]) }
  const inline = text.match(/(\d+)% de probabilidad/)
  if (inline) return { label: text, chance: Number(inline[1]), entryId: resolve(text) }
  return { label: text, guaranteed: /garantizad/i.test(text) ? true : undefined, entryId: resolve(text) }
}
